import type { IgfdPaymentPlan } from "api-types";
import { resolveProductImportQuota } from "api-types";

import { useMemo, useSyncExternalStore } from "react";
import { useSelector } from "react-redux";

import {
  isMixpanelFlagEnabled,
  subscribeMixpanelFlags,
} from "../mixpanel/mixpanel";
import type { Features, ReduxState } from "../types";

/**
 * A hook that provides access to features of the dashboard app.
 *
 * The features are derived from the restaurant's payment plan,
 * and access is controlled based on payment / flags set in the backend (kitchen)
 */
export const useFeatures: () => Features = () => {
  // We look at multiple slices of the state to derive the feature flags.
  const restaurantState = useSelector((state: ReduxState) => state.restaurant);
  const numLocations = useSelector(
    (state: ReduxState) => state.location.locations.length
  );

  return useMemo(() => {
    const selectedRestaurant = restaurantState.selectedRestaurant;
    // Every consumer renders under RestaurantManagementLayout, which guarantees
    // a selected restaurant whose plan is already in the store.
    const paymentPlan: IgfdPaymentPlan =
      restaurantState.paymentPlansByRestaurantUuid[selectedRestaurant!.uuid];
    return {
      analytics: paymentPlan.analytics,
      products: paymentPlan.products,
      allowManualAllergensAndPreferences:
        paymentPlan.allowManualAllergensAndPreferences,
      ingredientSearch: paymentPlan.ingredientSearch,
      dishImages: paymentPlan.dishImages,
      spiceLevels: paymentPlan.spiceLevels,
      dishNutritionDetails: paymentPlan.dishNutritionDetails,
      staffLinks: paymentPlan.staffLinks,
      productImportQuota: resolveProductImportQuota(paymentPlan),

      createLocationAllowed: paymentPlan.numLocations > numLocations,
    } as Features;
  }, [restaurantState, numLocations]);
};

/**
 * UI Feature flag, on when set via the admin modal or turned on for the
 * workspace in Mixpanel. This is not related to payment plans.
 */
export const useDashboardAppFeatureFlag: (
  flag: keyof ReduxState["dashboardApp"]["featureFlags"]
) => boolean = (flag: keyof ReduxState["dashboardApp"]["featureFlags"]) => {
  const featureFlags = useSelector(
    (state: ReduxState) => state.dashboardApp.featureFlags
  );
  const enabledInMixpanel = useSyncExternalStore(subscribeMixpanelFlags, () =>
    isMixpanelFlagEnabled(flag)
  );
  return featureFlags[flag] || enabledInMixpanel;
};
